import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';

/**
 * useNegotiationEvents - Realtime subscription for a single negotiation's event log.
 *
 * Fetches all rows from negotiation_events for the given negotiation, ordered by
 * created_at asc. Consumers filter by event_type as needed.
 * Re-fetches on channel error or disconnect.
 *
 * @param {string} negotiationId
 * @param {object} session - Auth session
 * @returns {{ events: Array, isLoading: boolean, error: Error|null }}
 */
export const useNegotiationEvents = (negotiationId, session) => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    if (!session?.user || !negotiationId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('negotiation_events')
        .select('*')
        .eq('negotiation_id', negotiationId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      setEvents(data || []);
      setError(null);
    } catch (err) {
      logger.error('[useNegotiationEvents] Fetch error:', err);
      setError(err);
      Sentry.captureException(err, {
        tags: { hook: 'useNegotiationEvents' },
        extra: { negotiationId },
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, negotiationId]);

  useEffect(() => {
    if (!session?.user || !negotiationId) {
      setEvents([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Subscribe BEFORE fetching to close the mount race: any negotiation_events
    // INSERT that fires between fetchEvents() returning and the channel being
    // SUBSCRIBED would otherwise be missed permanently. The reconcile fetch
    // runs inside the SUBSCRIBED callback (mirrors the fix applied to
    // useNegotiationRealtime in commit 89ccd6d).
    const channelName = `negotiation-events:${negotiationId}`;

    // Remove existing channel if any
    const existingChannels = supabase.getChannels();
    const existing = existingChannels.find((ch) => ch.topic === `realtime:${channelName}`);
    if (existing) {
      supabase.removeChannel(existing);
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiation_events',
          filter: `negotiation_id=eq.${negotiationId}`,
        },
        (payload) => {
          logger.log('[useNegotiationEvents] Event INSERT:', payload.new?.event_type);
          setEvents((prev) => {
            // Dedup check
            if (prev.find((e) => e.id === payload.new.id)) return prev;
            return [...prev, payload.new];
          });
        }
      )
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('[useNegotiationEvents] Subscribed to channel:', channelName);
          // Reconcile initial state once the channel is live. The setEvents
          // merge in the INSERT handler dedupes by id, so duplicates are safe.
          fetchEvents();
        }
        if (status === 'CHANNEL_ERROR') {
          logger.warn('[useNegotiationEvents] Channel error, re-fetching');
          fetchEvents();
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [session?.user?.id, negotiationId, fetchEvents]);

  return { events, isLoading, error };
};
